import React, { useMemo } from 'react';
import { format } from 'date-fns';
import {
  canNavigateYearPage,
  isMonthSelectable,
  isYearSelectable,
} from './datePickerBounds';

export const YEAR_GRID_SIZE = 12;
export const YEAR_GRID_OFFSET = 5;
export const MONTH_INDEXES = Array.from({ length: 12 }, (_unused, index) => index);

export const getYearRangeStart = (year) => year - YEAR_GRID_OFFSET;

export const getMonthShortLabel = (monthIndex) =>
  format(new Date(2020, monthIndex, 1), 'MMM');

function YearMonthNav({ view, currentYear, currentMonth, setView }) {
  return (
    <div className="header-title">
      <span
        className={`clickable${view === 'year' ? ' active' : ''}`}
        onClick={() => setView('year')}
      >
        {currentYear}
      </span>
      <span className="header-separator">-</span>
      <span
        className={`clickable${view === 'month' ? ' active' : ''}`}
        onClick={() => setView('month')}
      >
        {getMonthShortLabel(currentMonth)}
      </span>
    </div>
  );
}

function YearPageChevron({ direction }) {
  const path = direction === 'prev' ? 'M10 3L5 8L10 13' : 'M6 3L11 8L6 13';

  return (
    <svg
      className="year-page-btn__icon"
      width="14"
      height="14"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function IceaDatePickerHeader({
  view,
  setView,
  date,
  changeYear,
  changeMonth,
  yearRangeStart,
  setYearRangeStart,
  minDate,
  maxDate,
  onYearSelected,
  onMonthSelected,
}) {
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();

  const pageYears = useMemo(
    () =>
      Array.from({ length: YEAR_GRID_SIZE }, (_unused, index) => yearRangeStart + index),
    [yearRangeStart]
  );

  const selectableMonths = useMemo(
    () =>
      MONTH_INDEXES.filter((monthIndex) =>
        isMonthSelectable(currentYear, monthIndex, minDate, maxDate)
      ),
    [currentYear, minDate, maxDate]
  );

  const canGoPrev = canNavigateYearPage(
    yearRangeStart,
    -1,
    minDate,
    maxDate,
    YEAR_GRID_SIZE
  );
  const canGoNext = canNavigateYearPage(
    yearRangeStart,
    1,
    minDate,
    maxDate,
    YEAR_GRID_SIZE
  );

  const handleYearClick = (year) => {
    changeYear(year);
    onYearSelected?.(year);
    setView('month');
  };

  const handleMonthClick = (monthIndex) => {
    changeMonth(monthIndex);
    onMonthSelected?.(monthIndex);
    setView('day');
  };

  return (
    <>
      <div
        className={`icea-date-header year-grid-header${
          view === 'year' ? ' year-grid-header--with-nav' : ''
        }`}
      >
        {view === 'year' && (
          canGoPrev ? (
            <button
              type="button"
              className="year-page-btn year-page-btn--header year-page-btn--prev"
              aria-label="Previous years"
              onClick={() => setYearRangeStart(yearRangeStart - YEAR_GRID_SIZE)}
            >
              <YearPageChevron direction="prev" />
            </button>
          ) : (
            <span className="year-page-spacer" aria-hidden="true" />
          )
        )}

        <YearMonthNav
          view={view}
          currentYear={currentYear}
          currentMonth={currentMonth}
          setView={setView}
        />

        {view === 'year' && (
          canGoNext ? (
            <button
              type="button"
              className="year-page-btn year-page-btn--header year-page-btn--next"
              aria-label="Next years"
              onClick={() => setYearRangeStart(yearRangeStart + YEAR_GRID_SIZE)}
            >
              <YearPageChevron direction="next" />
            </button>
          ) : (
            <span className="year-page-spacer" aria-hidden="true" />
          )
        )}
      </div>

      {view === 'year' && (
        <div className="year-grid-panel">
          <div className="year-buttons">
            {pageYears.map((year) => {
              if (!isYearSelectable(year, minDate, maxDate)) {
                return <span key={year} className="year-btn year-btn--empty" aria-hidden="true" />;
              }

              return (
                <button
                  key={year}
                  type="button"
                  className={`year-btn ${year === currentYear ? 'active' : ''}`}
                  onClick={() => handleYearClick(year)}
                >
                  {year}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === 'month' && selectableMonths.length > 0 && (
        <div className="month-grid">
          {selectableMonths.map((monthIndex) => (
            <button
              key={monthIndex}
              type="button"
              className={`month-btn ${monthIndex === currentMonth ? 'active' : ''}`}
              onClick={() => handleMonthClick(monthIndex)}
            >
              {getMonthShortLabel(monthIndex)}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
